import { Pagination, Stack } from '@mui/material'
import { FundingRate, getFundingRateHistory } from 'api/v1/information'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { slice } from 'lodash'
import numbro from 'numbro'
import { memo, useEffect, useMemo } from 'react'
import { usePerpSymbols } from 'store/perp/hooks'
import { TokenSymbol } from 'types'
import { contractInformationSymbolAtom } from '../atom'
import { GridBody, GridBox, GridHead, GridItem } from './ContractInfo'
import { DateFormatter } from '../../../../utils/datetime'
import { styled } from '@mui/material'

export const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  .MuiPagination-ul .MuiPaginationItem-root.Mui-selected {
    background-color: unset;
    border: unset;
    color: #fff;
    font-size: 14px;
  }
  .MuiPagination-ul .MuiPaginationItem-root {
    color: rgba(149, 164, 173, 1);
  }
`

const fundingRateSymbolAtom = atom<TokenSymbol>('')
const fundingRateAtom = atom<Promise<FundingRate[]>>(async (get) => {
  const symbol = get(fundingRateSymbolAtom)
  if (!symbol) {
    return []
  }
  const r = await getFundingRateHistory(symbol).catch((e) => {
    return { data: { results: [] } }
  })

  return r.data.results
})

const pageAtom = atom(1)

export function useUpdateFundingBaseCurrency() {
  const contracts = usePerpSymbols()
  const setFundingRateSymbol = useSetAtom(fundingRateSymbolAtom)
  const currentSymbol = useAtomValue(contractInformationSymbolAtom)
  useEffect(() => {
    const contract = contracts.find((v) => v.name === currentSymbol)
    if (contract) {
      setFundingRateSymbol(contract.baseCurrency)
    }
  }, [currentSymbol, contracts, setFundingRateSymbol])
}

export function useFundingRatePagination(limit: number = 10) {
  const [page, setPage] = useAtom(pageAtom)
  const [insuranceFund] = useAtom(fundingRateAtom)

  return useMemo(() => {
    return {
      rows: slice(insuranceFund, (page - 1) * limit, page * limit),
      count: Math.ceil(insuranceFund.length / limit),
      page,
      go: setPage,
    }
  }, [insuranceFund, limit, page])
}

export const FundingRatePanel = memo(() => {
  useUpdateFundingBaseCurrency()
  const { rows, count, page, go } = useFundingRatePagination()
  useEffect(() => {
    go(1)
  }, [go])

  return (
    <GridBox>
      <GridHead>
        <GridItem xs={4} className="title">
          Time
        </GridItem>
        <GridItem xs={4} className="title">
          Funding Interval
        </GridItem>
        <GridItem xs={4} className="title">
          Funding Rate
        </GridItem>
      </GridHead>
      {rows?.map((item) => (
        <GridBody key={item[0]}>
          <GridItem xs={4}>{DateFormatter.toISODateTime(item[0])}</GridItem>
          <GridItem xs={4}>8h</GridItem>
          <GridItem xs={4}>
            {numbro(item[1]).format({
              output: 'percent',
              mantissa: 4,
            })}
          </GridItem>
        </GridBody>
      ))}

      <Stack
        sx={{ padding: '40px 0' }}
        display={'flex'}
        width={'100%'}
        justifyContent={'flex-end'}
      >
        <StyledPagination
          page={page}
          count={count}
          shape="rounded"
          onChange={(e, p) => {
            go(p)
          }}
        />
      </Stack>
      {/* <Pagination count={10} variant="outlined" shape="rounded" /> */}
    </GridBox>
  )
})
